'use client';
import React from 'react';
import classNames from 'classnames';
import { hasCldIcon, TypeBlockIconBlocks } from '@fm-nx/contentful-types';
import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import LinkWrapper from '../link-wrapper/link-wrapper';
import { CldImage } from 'next-cloudinary';
import TextMarkdown from '../text-markdown/text-markdown';
import AnimationFadeLazy from '../animation-fade-lazy/animation-fade-lazy';
import { LazyMotion, m as motion } from 'framer-motion';
import { variantsChildren } from '../__constants__/animation-variants';
const loadFeatures = () => import('../__constants__/framer-features').then((res) => res.default);

type IconBlocksPropTypes = {
    blockIconBlocks: TypeBlockIconBlocks<'WITHOUT_UNRESOLVABLE_LINKS'>;
    mode?: 'dark' | 'light';
};

export const IconBlocks = (props: IconBlocksPropTypes) => {
    const { blockIconBlocks, mode = 'light' } = props;
    const updatedBlock = useContentfulLiveUpdates(blockIconBlocks);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedBlock.sys.id });
    const { fields } = updatedBlock;

    if (!fields) return null;
    const {
        iconBlocks,
        overline,
        headerText,
        paragraph,
        numberOfColumnsOfBlocks = '2',
        iconColor,
        backgroundColor = 'none',
    } = fields;
    const isDark = mode === 'dark';

    const gridClasses = classNames({
        'grid-cols-1 lg:grid-cols-3 md:grid-cols-2 md:gap-6': numberOfColumnsOfBlocks === '3',
        'grid-cols-1 md:grid-cols-2': numberOfColumnsOfBlocks === '2' || (iconBlocks && iconBlocks.length <= 2),
    });

    const effectiveIconColor = iconColor || (mode === 'dark' ? 'white' : 'black');

    const iconColorClasses = classNames({
        'filter-chartreuse': effectiveIconColor === 'lime',
        'filter-black': effectiveIconColor === 'black',
        'filter-white': effectiveIconColor === 'white',
    });

    return (
        <AnimationFadeLazy>
            <section className={classNames('relative', { 'bg-ivory-100 py-12 md:py-16': backgroundColor === 'ivory' })}>
                <div className="w-full mx-auto max-w-container">
                    <div className="mx-6 my-14 md:mx-0">
                        <div className="flex flex-col md:grid md:grid-cols-14 md:gap-4">
                            <div className="col-span-14 md:col-span-12 md:col-start-2">
                                <div className={classNames(`flex`, { 'justify-center': isDark })}>
                                    <div className={classNames(`max-w-2xl space-y-6`, { ' text-center': isDark })}>
                                        {overline && (
                                            <h2
                                                className={classNames(`h5 overline`, { 'text-white': isDark })}
                                                {...inspectorProps({ fieldId: 'overline' })}
                                            >
                                                {overline}
                                            </h2>
                                        )}
                                        <h3
                                            className={classNames(`h-xl`, { 'text-white': isDark })}
                                            {...inspectorProps({ fieldId: 'headerText' })}
                                        >
                                            {headerText}
                                        </h3>
                                        <TextMarkdown
                                            className={classNames(`text-xl`, { 'text-white': isDark })}
                                            text={paragraph || ''}
                                            mode={mode}
                                            {...inspectorProps({ fieldId: 'paragraph' })}
                                        />
                                    </div>
                                </div>
                                {iconBlocks && (
                                    <div className={`grid ${gridClasses} gap-8 mt-10 space-between`}>
                                        {iconBlocks.map((block) => {
                                            if (!block) return null;

                                            const imageCloudinary = hasCldIcon(block.fields.icon?.fields)
                                                ? block.fields.icon?.fields.iconCloudinary[0]
                                                : null;

                                            return (
                                                <IconBlockItem
                                                    key={`icon-block-${block.sys.id}`}
                                                    block={block}
                                                    imageCloudinary={imageCloudinary}
                                                    iconColorClasses={iconColorClasses}
                                                    isDark={isDark}
                                                    mode={mode}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AnimationFadeLazy>
    );
};

type IconBlockItemProps = {
    block: NonNullable<TypeBlockIconBlocks<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']['iconBlocks']>[number];
    imageCloudinary: { public_id: string } | null | undefined;
    iconColorClasses: string;
    isDark: boolean;
    mode: 'dark' | 'light';
};

const IconBlockItem = ({ block, imageCloudinary, iconColorClasses, isDark, mode }: IconBlockItemProps) => {
    if (!block) return null;
    const updatedItem = useContentfulLiveUpdates(block);
    const itemInspectorProps = useContentfulInspectorMode({ entryId: updatedItem.sys.id });

    return (
        <LazyMotion features={loadFeatures}>
            <motion.div variants={variantsChildren}>
                <div className={`mb-6 space-y-6`}>
                    {imageCloudinary && (
                        <div className={`${iconColorClasses} aspect-square h-[40px] w-[40px]`}>
                            <CldImage src={`${imageCloudinary.public_id}`} width="40" height="40" format="svg" alt="" />
                        </div>
                    )}
                    <h4
                        className={classNames(`my-4 h-lg`, { 'text-white': isDark })}
                        {...itemInspectorProps({ fieldId: 'headerText' })}
                    >
                        {updatedItem.fields?.headerText}
                    </h4>
                    <p
                        className={classNames(`p-xl`, { 'text-white': isDark })}
                        {...itemInspectorProps({ fieldId: 'paragraph' })}
                    >
                        {updatedItem.fields?.paragraph}
                    </p>
                    {updatedItem.fields?.link && (
                        <LinkWrapper
                            page={updatedItem.fields?.link}
                            className={classNames(`inline-block mt-4 h5 emdash-push`, {
                                'text-white': isDark,
                                'emdash-push--dark': mode === 'dark',
                            })}
                        >
                            Read More <span className="sr-only">about {updatedItem.fields?.headerText}</span>
                        </LinkWrapper>
                    )}
                </div>
            </motion.div>
        </LazyMotion>
    );
};

export default IconBlocks;
